import { Controller } from '@hotwired/stimulus'

// Continuously scales a markdown card's font size to its content length so a
// short task action keeps its comfortable, readable size while a long,
// multi-table card (e.g. invoice remediation) shrinks just enough to stay
// scannable instead of becoming an oversized wall.
//
// The scale is relative to whatever base size the card renders at (14px inline,
// 16px in the detail view), so "short stays large" holds in every context.
// Headings are em-based and the .md-content--tight class tightens the vertical
// rhythm, so the whole card — including the tables, whose density controller
// uses em units — scales together. Tables additionally tighten by width via the
// table-density controller; the two are orthogonal (length vs. table shape).
const SHORT_WEIGHT = 360   // at or below this, no shrink (comfortable)
const LONG_WEIGHT = 2200   // at or above this, fully shrunk
const MIN_SCALE = 0.8      // floor on the relative scale
const MIN_PX = 12          // absolute floor so tables never get unreadable

export default class extends Controller {
  connect() {
    // Capture the unscaled base before we ever touch font-size.
    const base = parseFloat(getComputedStyle(this.element).fontSize)
    this.basePx = Number.isFinite(base) && base > 0 ? base : 14
    this.apply()
    // Opening or closing a fold (a task's Evidence) changes what is read at once.
    this.onToggle = () => this.apply()
    this.element.addEventListener('toggle', this.onToggle, true)
  }

  disconnect() {
    this.element.removeEventListener('toggle', this.onToggle, true)
  }

  apply() {
    const el = this.element

    // Content weight: visible characters, with tables and rows weighted up
    // since they take far more space per character than prose.
    // A closed fold shows only its summary line, so what it holds is not counted.
    const closed = Array.from(el.querySelectorAll('details:not([open])'))
      .filter(fold => !fold.parentElement?.closest('details:not([open])'))
    const hidden = closed.reduce((sum, fold) => {
      const summary = fold.querySelector(':scope > summary')
      sum.text += Math.max(0, (fold.textContent || '').trim().length - (summary?.textContent || '').trim().length)
      sum.tables += fold.querySelectorAll('table').length
      sum.rows += fold.querySelectorAll('tr').length
      return sum
    }, { text: 0, tables: 0, rows: 0 })
    const text = (el.textContent || '').trim().length - hidden.text
    const tables = el.querySelectorAll('table').length - hidden.tables
    const rows = el.querySelectorAll('tr').length - hidden.rows
    const weight = text + tables * 250 + rows * 30

    const scale = this.scaleFor(weight)

    if (scale >= 0.999) {
      // Short content: leave the comfortable base size untouched.
      el.style.fontSize = ''
      el.classList.remove('md-content--tight')
    } else {
      el.style.fontSize = `${Math.max(MIN_PX, this.basePx * scale)}px`
      el.classList.add('md-content--tight')
    }
  }

  scaleFor(weight) {
    if (weight <= SHORT_WEIGHT) return 1
    if (weight >= LONG_WEIGHT) return MIN_SCALE
    const t = (weight - SHORT_WEIGHT) / (LONG_WEIGHT - SHORT_WEIGHT)
    return 1 - t * (1 - MIN_SCALE)
  }
}
